import { useEffect, useMemo, useState } from "react";
import { Sparkles, X } from "lucide-react";

/**
 * Sticky launch-promo banner — sits above the navbar on the landing page.
 * Visible by default. Dismissable for the current browsing session.
 *
 * Strategy: compact key info to maximise primary-impact + drive scroll
 * to the dedicated #promo-lancement section.
 */
export default function PromoBanner({ promo }) {
  const [hidden, setHidden] = useState(false);

  // Restore dismissal across SPA navigation
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("promo_lancement_dismissed") === "1") setHidden(true);
  }, []);

  // Expose a CSS variable so the navbar can push itself down by the banner height
  useEffect(() => {
    const visible = promo?.active && !hidden;
    document.documentElement.style.setProperty("--promo-h", visible ? "40px" : "0px");
    return () => document.documentElement.style.setProperty("--promo-h", "0px");
  }, [promo?.active, hidden]);

  const daysLeft = useMemo(() => {
    if (!promo?.end_date_iso) return null;
    const end = new Date(promo.end_date_iso).getTime();
    const now = Date.now();
    return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
  }, [promo?.end_date_iso]);

  if (!promo?.active || hidden) return null;

  const slots = promo.slots_remaining;
  const slotsLabel =
    slots <= 0
      ? "Toutes les places sont prises"
      : slots === 1
      ? "Plus qu'1 place"
      : `Plus que ${slots} places`;

  const dismiss = (e) => {
    e.stopPropagation();
    setHidden(true);
    try {
      sessionStorage.setItem("promo_lancement_dismissed", "1");
    } catch {}
  };

  const scrollToPromo = () => {
    const el = document.getElementById("promo-lancement");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={scrollToPromo}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && scrollToPromo()}
      data-testid="promo-banner"
      className="fixed top-0 left-0 right-0 z-[55] h-10 flex items-center cursor-pointer select-none
                 bg-[#1F3535] text-white border-b border-[#D4AF37]/30 overflow-hidden"
      aria-label="Voir l'offre de lancement"
    >
      {/* gold radial accent */}
      <div
        aria-hidden="true"
        className="absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_right,rgba(212,175,55,0.18),transparent_70%)] pointer-events-none"
      />
      {/* animated subtle shine */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-[#D4AF37]/10 to-transparent animate-[shine_6s_ease-in-out_infinite]"
        style={{ animation: "promoShine 7s ease-in-out infinite" }}
      />
      <style>{`@keyframes promoShine{0%,100%{transform:translateX(-100%);opacity:0}50%{transform:translateX(100%);opacity:1}}`}</style>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 w-full flex items-center justify-between gap-3 text-[12px] sm:text-[13px]">
        <div className="flex items-center gap-2 sm:gap-4 font-body min-w-0">
          <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#E8D8A9] font-semibold whitespace-nowrap">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={2} />
            Lancement de saison
          </span>
          <span className="hidden sm:inline-block w-px h-3 bg-white/20" />
          <span className="font-semibold text-white whitespace-nowrap">
            <span className="font-mono-tech text-[#D4AF37]">−{promo.discount_pct}%</span>
            <span className="hidden sm:inline"> pendant 1 an</span>
            <span className="sm:hidden"> · 1 an</span>
          </span>
          <span className="hidden md:inline-block w-px h-3 bg-white/20" />
          <span
            className={`whitespace-nowrap font-medium ${
              slots <= 3 ? "text-[#FFB59A]" : "text-white/85"
            }`}
            data-testid="promo-banner-slots"
          >
            <span className="font-mono-tech">{slotsLabel}</span>
            <span className="hidden md:inline text-white/55"> sur {promo.slots_total}</span>
          </span>
          {daysLeft !== null && (
            <>
              <span className="hidden lg:inline-block w-px h-3 bg-white/20" />
              <span className="hidden lg:inline whitespace-nowrap text-white/70">
                <span className="font-mono-tech text-[#E8D8A9]">J−{daysLeft}</span>
                <span className="ml-1 text-white/55">avant clôture</span>
              </span>
            </>
          )}
          <span className="hidden xl:inline-flex items-center gap-1 ml-2 text-[11px] text-[#E8D8A9] font-semibold uppercase tracking-wider">
            Voir l'offre →
          </span>
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Masquer la promotion"
          data-testid="promo-banner-close"
          className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
